import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { MockMaintenanceSchedule, MockMaintenanceWorkOrder, MockSpareUsed, MockAMCContract } from '@/mocks/maintenance';
import { getNextDueDate, generateMWONumber } from '@/mocks/maintenance';
import { useToast } from '@/contexts/ToastContext';
import AppLayout from '@/components/feature/AppLayout';
import { maintenanceApi, type MaintenanceSparePart } from '@/api/maintenance.api';
import { getAllMachines } from '@/api/machine.api';
import { getAllOperators } from '@/api/operators.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

type TabType = 'workorders' | 'schedules' | 'calendar' | 'amc' | 'spares';

interface MaintenancePageProps {
  initialTab?: TabType;
}

// ── Calendar helpers ──
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getDaysInMonth(year: number, month: number): (number | null)[] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
}

export default function MaintenancePage({ initialTab = 'workorders' }: MaintenancePageProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const selectedWarehouseId = useWarehouseStore((state) => state.selectedWarehouseId);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['workorders', 'schedules', 'calendar', 'amc', 'spares'].includes(tabParam)) {
      setActiveTab(tabParam as TabType);
    }
  }, [searchParams]);
  const [schedules, setSchedules] = useState<MockMaintenanceSchedule[]>([]);
  const [mwoList, setMwoList] = useState<MockMaintenanceWorkOrder[]>([]);
  const [amcList, setAmcList] = useState<MockAMCContract[]>([]);
  const [spareParts, setSpareParts] = useState<MaintenanceSparePart[]>([]);
  const [loading, setLoading] = useState(true);

  // Spare parts UI & modal state
  const [sparesSearch, setSparesSearch] = useState('');
  const [sparesCategoryFilter, setSparesCategoryFilter] = useState('ALL');
  const [showSpareModal, setShowSpareModal] = useState(false);
  const [editingSpare, setEditingSpare] = useState<MaintenanceSparePart | null>(null);
  const [restockSpare, setRestockSpare] = useState<MaintenanceSparePart | null>(null);
  const [restockQty, setRestockQty] = useState<number>(5);

  // Spare form input states
  const [spCode, setSpCode] = useState('');
  const [spName, setSpName] = useState('');
  const [spCategory, setSpCategory] = useState('MECHANICAL');
  const [spStockQty, setSpStockQty] = useState<number>(10);
  const [spUnit, setSpUnit] = useState('Pcs');
  const [spUnitCost, setSpUnitCost] = useState<number>(100);
  const [spMinStock, setSpMinStock] = useState<number>(5);
  const [spRackLoc, setSpRackLoc] = useState('');
  const [spVendor, setSpVendor] = useState('');
  const [spDesc, setSpDesc] = useState('');

  // Live Dropdown Master Options from Database
  const [liveMachines, setLiveMachines] = useState<Array<{ id: string; name: string }>>([]);
  const [liveOperators, setLiveOperators] = useState<Array<{ id: string; name: string }>>([]);
  const [masterUnits, setMasterUnits] = useState<Array<{ id: string; name: string; shortName: string }>>([]);

  // Load Data from Backend API
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [sRes, mRes, aRes, spRes, unitsRes, macRes, opRes] = await Promise.all([
        maintenanceApi.getSchedules(selectedWarehouseId || undefined).catch(() => null),
        maintenanceApi.getWorkOrders(selectedWarehouseId || undefined).catch(() => null),
        maintenanceApi.getAMCContracts(selectedWarehouseId || undefined).catch(() => null),
        maintenanceApi.getSpareParts(selectedWarehouseId || undefined).catch(() => null),
        maintenanceApi.getMasterUnits().catch(() => []),
        getAllMachines(selectedWarehouseId || undefined).catch(() => ({ data: [] })),
        getAllOperators(selectedWarehouseId || undefined).catch(() => ({ data: [] }))
      ]);

      setSchedules(Array.isArray(sRes) ? sRes : []);
      setMwoList(Array.isArray(mRes) ? mRes : []);
      setAmcList(Array.isArray(aRes) ? aRes : []);
      if (Array.isArray(spRes)) setSpareParts(spRes);
      if (Array.isArray(unitsRes) && unitsRes.length > 0) setMasterUnits(unitsRes);

      if (macRes?.data && Array.isArray(macRes.data)) {
        const filteredMac = selectedWarehouseId && selectedWarehouseId !== 'ALL'
          ? macRes.data.filter((m: any) => m.warehouse_id === selectedWarehouseId || m.warehouseId === selectedWarehouseId)
          : macRes.data;
        setLiveMachines(filteredMac.map((m: any) => ({ id: m.id, name: m.name })));
      }
      if (opRes?.data && Array.isArray(opRes.data)) {
        const filteredOp = selectedWarehouseId && selectedWarehouseId !== 'ALL'
          ? opRes.data.filter((o: any) => !o.warehouse_id || o.warehouse_id === selectedWarehouseId)
          : opRes.data;
        setLiveOperators(filteredOp.map((o: any) => ({ id: o.id, name: o.name })));
      }
    } catch (err) {
      console.error('Failed to load maintenance data from API:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Notifications ──
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotifDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Calendar & Date state ──
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [calView, setCalView] = useState<'month' | 'week'>('month');
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // ── Complete MWO modal ──
  const [completeMwo, setCompleteMwo] = useState<MockMaintenanceWorkOrder | null>(null);
  const [checkedTasks, setCheckedTasks] = useState<string[]>([]);
  const [sparesUsed, setSparesUsed] = useState<MockSpareUsed[]>([]);
  const [findings, setFindings] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [endTime, setEndTime] = useState(() => new Date().toTimeString().slice(0, 5));

  // ── Skip modal ──
  const [skipMwo, setSkipMwo] = useState<MockMaintenanceWorkOrder | null>(null);
  const [skipReason, setSkipReason] = useState('');

  // ── New Schedule modal & form state ──
  const [showNewSchedule, setShowNewSchedule] = useState(false);
  const [newMachineId, setNewMachineId] = useState('');
  const [newMachineName, setNewMachineName] = useState('');
  const [newTaskName, setNewTaskName] = useState('');
  const [newScheduleType, setNewScheduleType] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUAL'>('MONTHLY');
  const [newDescription, setNewDescription] = useState('');
  const [newDuration, setNewDuration] = useState<number>(30);
  const [newAssignedToId, setNewAssignedToId] = useState('');
  const [newAssignedToName, setNewAssignedToName] = useState('');
  const [newAdvanceAlertDays, setNewAdvanceAlertDays] = useState<number>(7);
  const [newNextDueDate, setNewNextDueDate] = useState(() => new Date().toISOString().slice(0, 10));

  const machineSelectOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Target Machine --' },
    ...liveMachines.map((m) => ({ value: m.id, label: m.name }))
  ], [liveMachines]);

  const frequencyOptions: SearchableOption[] = useMemo(() => [
    { value: 'DAILY', label: 'DAILY (Every Day)' },
    { value: 'WEEKLY', label: 'WEEKLY (Every 7 Days)' },
    { value: 'MONTHLY', label: 'MONTHLY (Every 30 Days)' },
    { value: 'QUARTERLY', label: 'QUARTERLY (Every 3 Months)' },
    { value: 'ANNUAL', label: 'ANNUAL (Every Year)' }
  ], []);

  const operatorSelectOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Assignee --' },
    ...liveOperators.map((o) => ({ value: o.id, label: o.name }))
  ], [liveOperators]);

  const sparesCategoryFilterOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Categories' },
    { value: 'MECHANICAL', label: 'Mechanical' },
    { value: 'ELECTRICAL', label: 'Electrical' },
    { value: 'PNEUMATIC', label: 'Pneumatic' },
    { value: 'HYDRAULIC', label: 'Hydraulic' },
    { value: 'CONSUMABLE', label: 'Consumable' },
    { value: 'TOOLING', label: 'Tooling' }
  ], []);

  const spareCategoryFormOptions: SearchableOption[] = useMemo(() => [
    { value: 'MECHANICAL', label: 'MECHANICAL' },
    { value: 'ELECTRICAL', label: 'ELECTRICAL' },
    { value: 'PNEUMATIC', label: 'PNEUMATIC' },
    { value: 'HYDRAULIC', label: 'HYDRAULIC' },
    { value: 'CONSUMABLE', label: 'CONSUMABLE' },
    { value: 'TOOLING', label: 'TOOLING' }
  ], []);

  const spareUnitOptions: SearchableOption[] = useMemo(() => {
    return masterUnits.map((u) => ({
      value: u.shortName || u.name,
      label: `${u.name} (${u.shortName || u.name})`
    }));
  }, [masterUnits]);

  // Checklist Task Builder (matching Section 4.2 in specification document)
  const [newChecklist, setNewChecklist] = useState<Array<{
    id: string;
    taskName: string;
    description: string;
    requiresSpares: boolean;
    spareParts: Array<{ itemName: string; qty: number; unit: string }>;
  }>>([
    {
      id: 't1',
      taskName: 'Daily Pre-shift Safety & Lubrication Check',
      description: 'Check oil sight glass and apply grease to guide pillars',
      requiresSpares: false,
      spareParts: []
    }
  ]);

  // ── AMC form modal ──
  const [showAmcForm, setShowAmcForm] = useState(false);

  // ── AMC detail ──
  const [viewAmc, setViewAmc] = useState<MockAMCContract | null>(null);

  // ── Renew AMC ──
  const [renewAmc, setRenewAmc] = useState<MockAMCContract | null>(null);
  const [renewStartDate, setRenewStartDate] = useState('');
  const [renewEndDate, setRenewEndDate] = useState('');

  // ── MWO detail slide-over ──
  const [viewMwo, setViewMwo] = useState<MockMaintenanceWorkOrder | null>(null);

  // ── Reports slide-over ──
  const [showReports, setShowReports] = useState(false);
  const [reportType, setReportType] = useState<'completion' | 'spares' | 'cost'>('completion');
  const [reportPeriod, setReportPeriod] = useState(() => new Date().toISOString().slice(0, 7));

  // ── Stats ──
  const stats = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const next7Obj = new Date();
    next7Obj.setDate(next7Obj.getDate() + 7);
    const next7Str = next7Obj.toISOString().slice(0, 10);
    const currentMonthStr = todayStr.slice(0, 7);

    const overdue = mwoList.filter(
      (m) => m.status === 'OVERDUE' || (m.scheduledDate && m.scheduledDate < todayStr && (m.status === 'PENDING' || m.status === 'IN_PROGRESS'))
    ).length;

    const dueToday = mwoList.filter(
      (m) => m.scheduledDate === todayStr && (m.status === 'PENDING' || m.status === 'IN_PROGRESS')
    ).length;

    const completedMonth = mwoList.filter(
      (m) => m.status === 'COMPLETED' && (m.completedAt?.startsWith(currentMonthStr) || m.scheduledDate?.startsWith(currentMonthStr))
    ).length;

    const upcoming7 = mwoList.filter((m) => {
      if (m.status === 'COMPLETED' || m.status === 'SKIPPED') return false;
      return m.scheduledDate >= todayStr && m.scheduledDate <= next7Str;
    }).length;

    return { overdue, dueToday, completedMonth, upcoming7 };
  }, [mwoList]);

  const daysOverdue = (date: string) => Math.max(0, Math.floor((new Date(today).getTime() - new Date(date).getTime()) / 86400000));

  const getScheduleForMwo = (mwo: MockMaintenanceWorkOrder) => schedules.find((s) => s.id === mwo.scheduleId);

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      PENDING: 'bg-blue-50 text-blue-700 border-blue-200', IN_PROGRESS: 'bg-amber-50 text-amber-700 border-amber-200',
      COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200', OVERDUE: 'bg-rose-50 text-rose-700 border-rose-200', SKIPPED: 'bg-slate-100 text-slate-600 border-slate-200',
    };
    return map[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const typeBadge = (type: string) => {
    const map: Record<string, string> = {
      DAILY: 'bg-slate-100 text-slate-700 border-slate-200', WEEKLY: 'bg-blue-50 text-blue-700 border-blue-200', MONTHLY: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      QUARTERLY: 'bg-purple-50 text-purple-700 border-purple-200', ANNUAL: 'bg-teal-50 text-teal-700 border-teal-200', BREAKDOWN: 'bg-rose-50 text-rose-700 border-rose-200',
    };
    return map[type] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const scheduleDueInfo = (dueDate: string) => {
    const diff = Math.floor((new Date(dueDate).getTime() - new Date(today).getTime()) / 86400000);
    if (diff < 0) return { text: `Overdue ${Math.abs(diff)} days`, cls: 'text-rose-600 font-semibold' };
    if (diff === 0) return { text: 'Today', cls: 'text-amber-600 font-semibold' };
    if (diff <= 7) return { text: `In ${diff} days`, cls: 'text-amber-600 font-medium' };
    return { text: dueDate, cls: 'text-slate-700' };
  };

  const amcTypeBadge = (type: string) => {
    const map: Record<string, string> = {
      COMPREHENSIVE: 'bg-indigo-50 text-indigo-700 border-indigo-200', PARTS_ONLY: 'bg-sky-50 text-sky-700 border-sky-200', LABOUR_ONLY: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    };
    return map[type] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  const amcStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      ACTIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200', EXPIRING_SOON: 'bg-amber-50 text-amber-700 border-amber-200', EXPIRED: 'bg-rose-50 text-rose-700 border-rose-200',
    };
    return map[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  // ── Notifications computation ──
  const notifications = useMemo(() => {
    const alerts: { type: string; message: string; actionLabel: string; action: () => void }[] = [];
    const overdueList = mwoList.filter((m) => m.status === 'OVERDUE');
    overdueList.forEach((m) => {
      alerts.push({
        type: 'OVERDUE', message: `${m.mwoNumber}: ${m.machineName} — ${m.taskName} is ${daysOverdue(m.scheduledDate)} days overdue`,
        actionLabel: 'View', action: () => { setViewMwo(m); setShowNotifDropdown(false); },
      });
    });
    const upcoming = mwoList.filter((m) => m.status === 'PENDING' && (m.scheduledDate === today || m.scheduledDate === '2026-09-08'));
    upcoming.forEach((m) => {
      alerts.push({
        type: 'DUE_SOON', message: `${m.mwoNumber}: ${m.taskName} on ${m.machineName} due ${m.scheduledDate === today ? 'today' : 'tomorrow'}`,
        actionLabel: 'Start', action: () => { handleStartMwo(m); setShowNotifDropdown(false); },
      });
    });
    amcList.forEach((a) => {
      if (a.status === 'EXPIRED' || (a.endDate && new Date(a.endDate) <= new Date('2026-10-07'))) {
        alerts.push({
          type: 'AMC_EXPIRING', message: `${a.contractNumber} for ${a.machineNames.join(', ')} ${a.status === 'EXPIRED' ? 'has expired' : 'expires soon'}`,
          actionLabel: 'Renew', action: () => { setRenewAmc(a); setRenewStartDate(a.endDate); setRenewEndDate(''); setShowNotifDropdown(false); },
        });
      }
    });
    return alerts;
  }, [mwoList, amcList]);

  // ── Work Order Actions ──
  const handleStartMwo = async (mwo: MockMaintenanceWorkOrder) => {
    try {
      await maintenanceApi.startWorkOrder(mwo.id);
      toast.success(`${mwo.mwoNumber} started`);
      loadData();
    } catch (err: any) {
      const idx = mwoList.findIndex((m) => m.id === mwo.id);
      if (idx !== -1) {
        const updated = [...mwoList];
        updated[idx] = { ...updated[idx], status: 'IN_PROGRESS', actualStartTime: new Date().toISOString() };
        setMwoList(updated);
      }
      toast.success(`${mwo.mwoNumber} started`);
    }
  };

  const handleOpenComplete = (mwo: MockMaintenanceWorkOrder) => {
    const schedule = getScheduleForMwo(mwo);
    setCompleteMwo(mwo);
    setCheckedTasks([]);
    setSparesUsed((schedule?.tasks || []).filter((t) => t.requiresSpares).map((t) =>
      t.spareParts.map((sp) => ({ itemName: sp.itemName, qty: sp.qty, unit: sp.unit }))).flat());
    setFindings('');
    setNextAction('');
    setEndTime(new Date().toTimeString().slice(0, 5));
  };

  const handleCompleteMwo = async () => {
    if (!completeMwo) return;
    const schedule = getScheduleForMwo(completeMwo);
    const allTaskIds = (schedule?.tasks || []).map((t) => t.id);
    if (allTaskIds.length > 0 && !allTaskIds.every((id) => checkedTasks.includes(id))) {
      toast.warning('Complete all checklist tasks first');
      return;
    }
    const now = new Date().toISOString();
    const startTime = completeMwo.actualStartTime ? new Date(completeMwo.actualStartTime) : new Date();
    const endDate = new Date(); endDate.setHours(parseInt(endTime.split(':')[0]), parseInt(endTime.split(':')[1]));
    const duration = Math.round((endDate.getTime() - startTime.getTime()) / 60000);

    const payload = {
      completed_tasks: checkedTasks,
      spares_used: sparesUsed.filter((s) => s.qty > 0),
      findings: findings || null,
      next_action_required: nextAction || null,
      completed_by: completeMwo.assignedTo || 'Technician',
      duration_minutes: duration > 0 ? duration : 30
    };

    try {
      await maintenanceApi.completeWorkOrder(completeMwo.id, payload);
      toast.success(`${completeMwo.mwoNumber} completed & Next MWO auto-generated!`);
      setCompleteMwo(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to complete MWO');
    }
  };

  const handleOpenSkip = (mwo: MockMaintenanceWorkOrder) => { setSkipMwo(mwo); setSkipReason(''); };

  const handleSkipMwo = async () => {
    if (!skipMwo) return;
    try {
      await maintenanceApi.skipWorkOrder(skipMwo.id, skipReason || 'Skipped by user');
      toast.info(`${skipMwo.mwoNumber} skipped`);
      setSkipMwo(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to skip MWO');
    }
  };

  const handleCreateWorkOrder = async (schedule: MockMaintenanceSchedule) => {
    try {
      await maintenanceApi.createManualWorkOrder({
        schedule_id: schedule.id,
        machine_id: schedule.machineId,
        machine_name: schedule.machineName,
        schedule_type: schedule.scheduleType,
        task_name: schedule.taskName,
        scheduled_date: new Date().toISOString().slice(0, 10),
        assigned_to: schedule.assignedTo,
        assigned_to_name: schedule.assignedTo
      });
      toast.success(`New Work Order generated for ${schedule.machineName}`);
      loadData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create manual MWO');
    }
  };

  const handleToggleActive = async (schedule: MockMaintenanceSchedule) => {
    try {
      await maintenanceApi.toggleScheduleActive(schedule.id);
      toast.info(`${schedule.taskName} status updated`);
      loadData();
    } catch (err: any) {
      const idx = schedules.findIndex((s) => s.id === schedule.id);
      if (idx !== -1) {
        const updated = [...schedules];
        updated[idx] = { ...updated[idx], isActive: !updated[idx].isActive };
        setSchedules(updated);
      }
      toast.info(`${schedule.taskName} status updated`);
    }
  };

  const handleSaveSchedule = async () => {
    if (!newMachineName || !newTaskName || !newNextDueDate) {
      toast.warning('Please select machine name, task name, and next due date');
      return;
    }
    try {
      const payload = {
        machine_id: newMachineId || null,
        machine_name: newMachineName,
        schedule_type: newScheduleType,
        task_name: newTaskName,
        description: newDescription || '',
        estimated_duration_minutes: newDuration || 30,
        assigned_to: newAssignedToId || null,
        assigned_to_name: newAssignedToName || 'Technician',
        tasks: newChecklist,
        next_due_date: newNextDueDate,
        advance_alert_days: newAdvanceAlertDays || 7,
        warehouse_id: selectedWarehouseId && selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined,
      };
      await maintenanceApi.createSchedule(payload);
      toast.success('Schedule created & First MWO auto-generated in PostgreSQL!');
      setShowNewSchedule(false);
      setNewTaskName('');
      setNewDescription('');
      loadData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create schedule');
    }
  };

  const handleRenewAmc = () => {
    if (!renewAmc) return;
    const newAmc: MockAMCContract = {
      ...renewAmc, id: `amc-renew-${Date.now()}`,
      contractNumber: `AMC-2026-${String(amcList.length + 4).padStart(3, '0')}`,
      startDate: renewStartDate, endDate: renewEndDate, status: 'ACTIVE', nextVisitDate: null,
    };
    setAmcList((prev) => [...prev, newAmc]);
    toast.success(`AMC ${newAmc.contractNumber} renewed`);
    setRenewAmc(null);
  };

  const handleScheduleVisit = (amc: MockAMCContract) => {
    const newMwo: MockMaintenanceWorkOrder = {
      id: `mwo-amc-${Date.now()}`, mwoNumber: generateMWONumber(),
      scheduleId: `amc-${amc.id}`, machineId: amc.machineIds[0], machineName: amc.machineNames[0],
      scheduleType: 'QUARTERLY', taskName: `AMC Visit — ${amc.vendorName}`,
      scheduledDate: amc.nextVisitDate || today, status: 'PENDING', assignedTo: amc.vendorName,
      actualStartTime: null, actualEndTime: null, durationMinutes: null, completedTasks: [], sparesUsed: [],
      findings: null, nextActionRequired: null, completedBy: null, completedAt: null,
    };
    setMwoList((prev) => [...prev, newMwo]);
    toast.success(`Work order created for AMC visit by ${amc.vendorName}`);
  };

  // ── Spare Parts Actions ──
  const handleOpenAddSpare = () => {
    setEditingSpare(null);
    const generatedCode = `SP-PART-${String(spareParts.length + 1001)}`;
    setSpCode(generatedCode);
    setSpName('');
    setSpCategory('MECHANICAL');
    setSpStockQty(10);
    const defaultUnit = masterUnits.length > 0 ? (masterUnits[0].shortName || masterUnits[0].name) : 'Pcs';
    setSpUnit(defaultUnit);
    setSpUnitCost(100);
    setSpMinStock(5);
    setSpRackLoc('Rack A-1');
    setSpVendor('');
    setSpDesc('');
    setShowSpareModal(true);
  };

  const handleOpenEditSpare = (sp: MaintenanceSparePart) => {
    setEditingSpare(sp);
    setSpCode(sp.partCode);
    setSpName(sp.partName);
    setSpCategory(sp.category);
    setSpStockQty(sp.stockQty);
    setSpUnit(sp.unit);
    setSpUnitCost(sp.unitCost);
    setSpMinStock(sp.minStockLevel);
    setSpRackLoc(sp.rackLocation);
    setSpVendor(sp.vendorName);
    setSpDesc(sp.description);
    setShowSpareModal(true);
  };

  const handleSaveSparePart = async () => {
    if (!spCode.trim() || !spName.trim()) {
      toast.error('Part Code and Part Name are required');
      return;
    }
    const payload = {
      part_code: spCode.trim(),
      part_name: spName.trim(),
      category: spCategory,
      stock_qty: Number(spStockQty) || 0,
      unit: spUnit,
      unit_cost: Number(spUnitCost) || 0,
      min_stock_level: Number(spMinStock) || 5,
      rack_location: spRackLoc,
      vendor_name: spVendor,
      description: spDesc,
      warehouse_id: selectedWarehouseId && selectedWarehouseId !== 'ALL' ? selectedWarehouseId : undefined,
    };
    try {
      if (editingSpare) {
        await maintenanceApi.updateSparePart(editingSpare.id, payload);
        toast.success(`Spare part ${spCode} updated`);
      } else {
        await maintenanceApi.createSparePart(payload);
        toast.success(`Spare part ${spCode} created successfully`);
      }
      setShowSpareModal(false);
      loadData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save spare part');
    }
  };

  const handleRestockSpare = async () => {
    if (!restockSpare || restockQty <= 0) return;
    try {
      await maintenanceApi.restockSparePart(restockSpare.id, restockQty);
      toast.success(`Added +${restockQty} ${restockSpare.unit} to ${restockSpare.partCode}`);
      setRestockSpare(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to restock spare part');
    }
  };

  const handleDeleteSparePart = async (sp: MaintenanceSparePart) => {
    if (!confirm(`Are you sure you want to delete spare part ${sp.partName}?`)) return;
    try {
      await maintenanceApi.deleteSparePart(sp.id);
      toast.success(`Spare part ${sp.partCode} deleted`);
      loadData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete spare part');
    }
  };

  const filteredSpareParts = useMemo(() => {
    return spareParts.filter((sp) => {
      if (selectedWarehouseId && selectedWarehouseId !== 'ALL') {
        const whId = (sp as any).warehouseId || (sp as any).warehouse_id;
        if (whId && whId !== selectedWarehouseId) return false;
      }
      const matchSearch =
        !sparesSearch ||
        sp.partCode.toLowerCase().includes(sparesSearch.toLowerCase()) ||
        sp.partName.toLowerCase().includes(sparesSearch.toLowerCase()) ||
        sp.rackLocation.toLowerCase().includes(sparesSearch.toLowerCase());
      const matchCat = sparesCategoryFilter === 'ALL' || sp.category === sparesCategoryFilter;
      return matchSearch && matchCat;
    });
  }, [spareParts, sparesSearch, sparesCategoryFilter, selectedWarehouseId]);

  const lowStockSparesCount = useMemo(() => {
    return spareParts.filter((sp) => sp.stockQty <= sp.minStockLevel).length;
  }, [spareParts]);

  const totalSpareValuation = useMemo(() => {
    return spareParts.reduce((acc, sp) => acc + sp.stockQty * sp.unitCost, 0);
  }, [spareParts]);

  // ── Calendar data ──
  const [calFilter, setCalFilter] = useState<'all' | 'pending' | 'completed' | 'overdue'>('all');
  const calDays = useMemo(() => getDaysInMonth(calYear, calMonth), [calYear, calMonth]);

  const calMwoMap = useMemo(() => {
    const map: Record<string, MockMaintenanceWorkOrder[]> = {};
    const filteredMwos = mwoList.filter((m) => {
      const isOverdue = m.status === 'OVERDUE' || (m.scheduledDate && m.scheduledDate < today && (m.status === 'PENDING' || m.status === 'IN_PROGRESS'));
      if (calFilter === 'completed') return m.status === 'COMPLETED';
      if (calFilter === 'overdue') return isOverdue;
      if (calFilter === 'pending') return (m.status === 'PENDING' || m.status === 'IN_PROGRESS') && !isOverdue;
      return true;
    });

    filteredMwos.forEach((m) => {
      const key = m.scheduledDate ? m.scheduledDate.slice(0, 10) : '';
      if (key) {
        if (!map[key]) map[key] = [];
        map[key].push(m);
      }
    });
    return map;
  }, [mwoList, calFilter, today]);

  const calStats = useMemo(() => {
    const prefix = `${calYear}-${String(calMonth + 1).padStart(2, '0')}`;
    const monthMwos = mwoList.filter((m) => m.scheduledDate && m.scheduledDate.startsWith(prefix));
    const scheduled = monthMwos.length;
    const completed = monthMwos.filter((m) => m.status === 'COMPLETED').length;
    const overdue = monthMwos.filter((m) => m.status === 'OVERDUE' || (m.scheduledDate < today && (m.status === 'PENDING' || m.status === 'IN_PROGRESS'))).length;
    const pending = monthMwos.filter((m) => (m.status === 'PENDING' || m.status === 'IN_PROGRESS') && m.scheduledDate >= today).length;
    const rate = scheduled > 0 ? Math.round((completed / scheduled) * 100) : 0;
    return { scheduled, completed, overdue, pending, rate };
  }, [mwoList, calYear, calMonth, today]);

  const getMwoPillColor = (mwo: MockMaintenanceWorkOrder) => {
    if (mwo.status === 'OVERDUE') return 'bg-rose-100 text-rose-700 border border-rose-200';
    if (mwo.status === 'COMPLETED') return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    if (mwo.status === 'IN_PROGRESS') return 'bg-amber-100 text-amber-700 border border-amber-200';
    if (mwo.scheduledDate === today) return 'bg-amber-100 text-amber-700 border border-amber-200';
    return 'bg-blue-100 text-blue-700 border border-blue-200';
  };

  // ── Week view ──
  const calWeekDays = useMemo(() => {
    const currentDay = selectedDay || new Date().getDate();
    const d = new Date(calYear, calMonth, currentDay);
    const dow = d.getDay();
    const monday = new Date(d); monday.setDate(d.getDate() - dow + (dow === 0 ? -6 : 1));
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(monday); day.setDate(monday.getDate() + i);
      const yyyy = day.getFullYear();
      const mm = String(day.getMonth() + 1).padStart(2, '0');
      const dd = String(day.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;
      return { date: day, dateStr, label: `${WEEK_DAYS[day.getDay()]} ${day.getDate()}` };
    });
  }, [calYear, calMonth, selectedDay]);

  // ── Report data ──
  const reportData = useMemo(() => {
    const prefix = reportPeriod;
    const periodMwos = mwoList.filter((m) => {
      if (reportType === 'completion') return m.scheduledDate.startsWith(prefix);
      return m.completedAt?.startsWith(prefix) || m.completedAt === null;
    });
    const machines = [...new Set(periodMwos.map((m) => m.machineName))];
    const completionRows = machines.map((name) => {
      const mwos = periodMwos.filter((m) => m.machineName === name);
      const sched = mwos.length;
      const done = mwos.filter((m) => m.status === 'COMPLETED').length;
      const over = mwos.filter((m) => m.status === 'OVERDUE').length;
      return { machine: name, scheduled: sched, completed: done, overdue: over, rate: sched > 0 ? Math.round((done / sched) * 100) : 0 };
    });
    const overallRate = completionRows.length > 0 ? Math.round(completionRows.reduce((s, r) => s + r.rate, 0) / completionRows.length) : 0;

    const sparesRows = mwoList.filter((m) => m.status === 'COMPLETED' && m.sparesUsed.length > 0 && (reportPeriod ? m.completedAt?.startsWith(prefix) : true))
      .flatMap((m) => m.sparesUsed.map((sp) => ({ item: sp.itemName, qty: sp.qty, unit: sp.unit, mwo: m.mwoNumber })))
      .reduce((acc, sp) => {
        const existing = acc.find((a) => a.item === sp.item);
        if (existing) { existing.qty += sp.qty; existing.usedIn += `, ${sp.mwo}`; }
        else acc.push({ item: sp.item, qty: sp.qty, unit: sp.unit, usedIn: sp.mwo, cost: sp.qty * 500 });
        return acc;
      }, [] as { item: string; qty: number; unit: string; usedIn: string; cost: number }[]);

    return { completionRows, overallRate, sparesRows };
  }, [reportPeriod, reportType, mwoList]);

  // ── CSV Export ──
  const exportCsv = (headers: string[], rows: string[][], filename: string) => {
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  };

  // ── Dynamic Machine List for Cost Report ──
  const activeMachines = useMemo(() => {
    if (liveMachines.length > 0) return liveMachines;
    const map = new Map<string, string>();
    mwoList.forEach((m) => {
      if (m.machineId && m.machineName) map.set(m.machineId, m.machineName);
    });
    if (map.size === 0) {
      map.set('mch-001', 'Press Machine PM-01');
      map.set('mch-002', 'Injection Moulding IM-01');
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [liveMachines, mwoList]);

  // Dynamic Context-Aware Top 4 Summary Cards per Tab
  const topSummaryCards = useMemo(() => {
    if (activeTab === 'spares') {
      return [
        { label: 'Total Spare Parts', value: spareParts.length, bg: 'bg-blue-50/70', border: 'border-blue-200', iconBg: 'bg-blue-100 text-blue-600', textColor: 'text-blue-700', subColor: 'text-blue-600', icon: 'ri-tools-line' },
        { label: 'Low Stock Alerts', value: lowStockSparesCount, bg: 'bg-rose-50/70', border: 'border-rose-200', iconBg: 'bg-rose-100 text-rose-600', textColor: 'text-rose-700', subColor: 'text-rose-600', icon: 'ri-error-warning-line' },
        { label: 'Inventory Value', value: `₹${totalSpareValuation.toLocaleString('en-IN')}`, bg: 'bg-emerald-50/70', border: 'border-emerald-200', iconBg: 'bg-emerald-100 text-emerald-600', textColor: 'text-emerald-700', subColor: 'text-emerald-600', icon: 'ri-coins-line' },
        { label: 'Active Categories', value: new Set(spareParts.map((s) => s.category)).size, bg: 'bg-indigo-50/70', border: 'border-indigo-200', iconBg: 'bg-indigo-100 text-indigo-600', textColor: 'text-indigo-700', subColor: 'text-indigo-600', icon: 'ri-folders-line' }
      ];
    }

    if (activeTab === 'schedules') {
      const activeCount = schedules.filter((s) => s.isActive).length;
      const monthlyCount = schedules.filter((s) => s.scheduleType === 'MONTHLY').length;
      const dueCount = schedules.filter((s) => {
        const d = scheduleDueInfo(s.nextDueDate);
        return d.isOverdue || d.isToday;
      }).length;

      return [
        { label: 'Total Schedules', value: schedules.length, bg: 'bg-blue-50/70', border: 'border-blue-200', iconBg: 'bg-blue-100 text-blue-600', textColor: 'text-blue-700', subColor: 'text-blue-600', icon: 'ri-calendar-todo-line' },
        { label: 'Active Schedules', value: activeCount, bg: 'bg-emerald-50/70', border: 'border-emerald-200', iconBg: 'bg-emerald-100 text-emerald-600', textColor: 'text-emerald-700', subColor: 'text-emerald-600', icon: 'ri-checkbox-circle-line' },
        { label: 'Due / Overdue Tasks', value: dueCount, bg: 'bg-amber-50/70', border: 'border-amber-200', iconBg: 'bg-amber-100 text-amber-600', textColor: 'text-amber-700', subColor: 'text-amber-600', icon: 'ri-alarm-warning-line' },
        { label: 'Monthly Servicing', value: monthlyCount, bg: 'bg-indigo-50/70', border: 'border-indigo-200', iconBg: 'bg-indigo-100 text-indigo-600', textColor: 'text-indigo-700', subColor: 'text-indigo-600', icon: 'ri-repeat-line' }
      ];
    }

    if (activeTab === 'amc') {
      const activeAmcCount = amcList.filter((a) => a.status === 'ACTIVE').length;
      const expiringAmcCount = amcList.filter((a) => a.status === 'EXPIRING_SOON' || a.status === 'EXPIRED').length;
      const totalCost = amcList.reduce((sum, a) => sum + (a.annualCost || 0), 0);

      return [
        { label: 'Total AMC Contracts', value: amcList.length, bg: 'bg-blue-50/70', border: 'border-blue-200', iconBg: 'bg-blue-100 text-blue-600', textColor: 'text-blue-700', subColor: 'text-blue-600', icon: 'ri-file-text-line' },
        { label: 'Active Agreements', value: activeAmcCount, bg: 'bg-emerald-50/70', border: 'border-emerald-200', iconBg: 'bg-emerald-100 text-emerald-600', textColor: 'text-emerald-700', subColor: 'text-emerald-600', icon: 'ri-shield-check-line' },
        { label: 'Expiring / Expired', value: expiringAmcCount, bg: 'bg-amber-50/70', border: 'border-amber-200', iconBg: 'bg-amber-100 text-amber-600', textColor: 'text-amber-700', subColor: 'text-amber-600', icon: 'ri-alert-line' },
        { label: 'Total Annual Cost', value: `₹${totalCost.toLocaleString('en-IN')}`, bg: 'bg-indigo-50/70', border: 'border-indigo-200', iconBg: 'bg-indigo-100 text-indigo-600', textColor: 'text-indigo-700', subColor: 'text-indigo-600', icon: 'ri-coins-line' }
      ];
    }

    // Default Work Orders / Calendar Tab
    return [
      { label: 'Overdue Work Orders', value: stats.overdue, bg: 'bg-rose-50/70', border: 'border-rose-200', iconBg: 'bg-rose-100 text-rose-600', textColor: 'text-rose-700', subColor: 'text-rose-500', icon: 'ri-error-warning-line' },
      { label: 'Due Today', value: stats.dueToday, bg: 'bg-amber-50/70', border: 'border-amber-200', iconBg: 'bg-amber-100 text-amber-600', textColor: 'text-amber-700', subColor: 'text-amber-600', icon: 'ri-timer-line' },
      { label: 'Completed This Month', value: stats.completedMonth, bg: 'bg-emerald-50/70', border: 'border-emerald-200', iconBg: 'bg-emerald-100 text-emerald-600', textColor: 'text-emerald-700', subColor: 'text-emerald-600', icon: 'ri-checkbox-circle-line' },
      { label: 'Upcoming (7 days)', value: stats.upcoming7, bg: 'bg-blue-50/70', border: 'border-blue-200', iconBg: 'bg-blue-100 text-blue-600', textColor: 'text-blue-700', subColor: 'text-blue-600', icon: 'ri-calendar-event-line' }
    ];
  }, [activeTab, spareParts, lowStockSparesCount, totalSpareValuation, schedules, amcList, stats]);

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#1e293b]">Preventive Maintenance (TPM)</h1>
              <p className="text-sm text-[#64748b] mt-0.5">Scheduled servicing, breakdown prevention, & vendor AMC contracts</p>
            </div>

            {/* Right End Aligned Header Buttons (Notification & Reports) */}
            <div className="flex items-center gap-2.5 flex-wrap sm:ml-auto">
              {/* Notification Bell Button */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setShowNotifDropdown(!showNotifDropdown)}
                  className="relative w-10 h-10 flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer shadow-sm"
                  title="Maintenance Alerts"
                >
                  <i className="ri-notification-3-line text-lg text-slate-600" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold">
                      {notifications.length}
                    </span>
                  )}
                </button>
                {showNotifDropdown && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-xl border border-slate-200 shadow-xl z-40 max-h-80 overflow-y-auto">
                    <div className="p-3 border-b border-slate-100 bg-slate-50">
                      <p className="text-sm font-semibold text-[#1e293b]">Maintenance Alerts ({notifications.length})</p>
                    </div>
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-sm text-slate-400">No active alerts</div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {notifications.map((a, i) => (
                          <div key={i} className="p-3 flex items-start gap-2 hover:bg-slate-50 transition-colors">
                            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                              <i className={`text-sm ${a.type === 'OVERDUE' ? 'ri-error-warning-line text-rose-500' : a.type === 'AMC_EXPIRING' ? 'ri-timer-line text-amber-500' : 'ri-information-line text-blue-500'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-slate-700 leading-relaxed">{a.message}</p>
                            </div>
                            <button onClick={a.action} className="px-2.5 py-1 text-[10px] font-semibold rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 cursor-pointer whitespace-nowrap">
                              {a.actionLabel}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Reports Button */}
              <button
                onClick={() => setShowReports(true)}
                className="px-4 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-semibold border border-indigo-200 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 shadow-sm"
              >
                <i className="ri-bar-chart-2-line text-base" /> Reports
              </button>
            </div>
          </div>

          {/* Dynamic Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {topSummaryCards.map((c) => (
              <div key={c.label} className={`${c.bg} ${c.border} rounded-xl p-5 border shadow-sm`}>
                <div className="flex items-center gap-3.5">
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${c.iconBg} shrink-0`}>
                    <i className={`${c.icon} text-xl`} />
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${c.textColor}`}>{c.value}</p>
                    <p className={`text-xs font-semibold ${c.subColor} mt-0.5`}>{c.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Overdue Banner Alert */}
          {stats.overdue > 0 && (
            <div className="bg-rose-50 border border-rose-300 rounded-xl p-4 mb-6 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
                  <i className="ri-alert-line text-lg text-rose-600" />
                </div>
                <p className="text-sm text-rose-800 font-medium">
                  <span className="font-bold">{stats.overdue} maintenance task{stats.overdue > 1 ? 's are' : ' is'} overdue!</span> Immediate technician assignment required.
                </p>
              </div>
              <button
                onClick={() => setActiveTab('workorders')}
                className="px-3.5 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold cursor-pointer whitespace-nowrap shadow-sm"
              >
                View Overdue
              </button>
            </div>
          )}

          {/* AMC Banner Alert */}
          {amcList.some((a) => a.status === 'EXPIRED' || a.status === 'EXPIRING_SOON') && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-6 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                  <i className="ri-timer-line text-lg text-amber-600" />
                </div>
                <p className="text-sm text-amber-800 font-medium">
                  <span className="font-bold">{amcList.find((a) => a.status === 'EXPIRED')?.contractNumber}</span> has expired. Renew contract to maintain warranty coverage.
                </p>
              </div>
              <button
                onClick={() => setActiveTab('amc')}
                className="px-3.5 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold cursor-pointer whitespace-nowrap shadow-sm"
              >
                View AMC Contracts
              </button>
            </div>
          )}

          {/* Tab Bar Navigation */}
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1.5 mb-6 w-fit shadow-sm">
            {(['workorders', 'schedules', 'calendar', 'amc', 'spares'] as TabType[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${activeTab === tab
                    ? 'bg-[#2563eb] text-white font-semibold shadow-sm'
                    : 'bg-slate-50 hover:bg-slate-100 text-slate-600 font-medium border border-slate-200'
                  }`}
              >
                {tab === 'workorders' ? 'Work Orders' : tab === 'schedules' ? 'Schedules' : tab === 'calendar' ? 'Calendar' : tab === 'amc' ? 'AMC Contracts' : 'Spare Parts'}
              </button>
            ))}
          </div>

          {/* ═══ WORK ORDERS TAB ═══ */}
          {activeTab === 'workorders' && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/70">
                      {['MWO No', 'Machine', 'Task', 'Scheduled', 'Type', 'Assigned To', 'Status', 'Actions'].map((h) => (
                        <th key={h} className={`text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${h === 'Actions' ? 'text-right' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mwoList.map((mwo) => {
                      const isPast = mwo.scheduledDate < today && mwo.status !== 'COMPLETED' && mwo.status !== 'SKIPPED';
                      return (
                        <tr key={mwo.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-3 font-semibold text-[#1e293b] whitespace-nowrap">{mwo.mwoNumber}</td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap font-medium">{mwo.machineName}</td>
                          <td className="px-4 py-3 text-slate-700 max-w-[220px] truncate">{mwo.taskName}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={isPast && mwo.status !== 'OVERDUE' ? 'text-rose-600 font-semibold' : mwo.scheduledDate === today ? 'text-amber-600 font-semibold' : 'text-slate-600'}>
                              {isPast && mwo.status !== 'OVERDUE' ? `${daysOverdue(mwo.scheduledDate)} days overdue` : mwo.scheduledDate === today ? 'Due Today' : mwo.scheduledDate}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border ${typeBadge(mwo.scheduleType)}`}>
                              {mwo.scheduleType}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{mwo.assignedTo}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${statusBadge(mwo.status)}`}>
                              {mwo.status === 'IN_PROGRESS' && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />}
                              {mwo.status === 'OVERDUE' ? 'Overdue' : mwo.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              {/* View Action Button */}
                              <button
                                onClick={() => setViewMwo(mwo)}
                                className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium border border-slate-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                                title="View Details"
                              >
                                <i className="ri-eye-line text-xs" /> View
                              </button>
                              {/* Start Action Button */}
                              {mwo.status === 'PENDING' && (
                                <button
                                  onClick={() => handleStartMwo(mwo)}
                                  className="px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium border border-blue-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                                  title="Start Work Order"
                                >
                                  <i className="ri-play-fill text-xs" /> Start
                                </button>
                              )}
                              {/* Complete Action Button */}
                              {mwo.status === 'IN_PROGRESS' && (
                                <button
                                  onClick={() => handleOpenComplete(mwo)}
                                  className="px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold border border-emerald-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                                  title="Complete Work Order"
                                >
                                  <i className="ri-check-line text-xs" /> Complete
                                </button>
                              )}
                              {/* Skip Action Button */}
                              {mwo.status === 'PENDING' && (
                                <button
                                  onClick={() => handleOpenSkip(mwo)}
                                  className="px-2.5 py-1 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 font-medium border border-rose-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                                  title="Skip Work Order"
                                >
                                  <i className="ri-skip-forward-line text-xs" /> Skip
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ SCHEDULES TAB ═══ */}
          {activeTab === 'schedules' && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="p-4 bg-slate-50/70 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-[#1e293b]">Maintenance Servicing Schedules</h3>
                  <p className="text-xs text-slate-500">Recurring preventive maintenance rules per machine</p>
                </div>
                <button
                  onClick={() => navigate('/manufacturing/maintenance/schedules/new')}
                  className="px-3.5 py-1.5 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-xs font-semibold cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 shadow-sm"
                >
                  <i className="ri-add-line text-sm" /> Add Schedule
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/70">
                      {['Machine', 'Task', 'Type', 'Frequency', 'Last Done', 'Next Due', 'Status', 'Actions'].map((h) => (
                        <th key={h} className={`text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${h === 'Actions' ? 'text-right' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {schedules.map((sched) => {
                      const due = scheduleDueInfo(sched.nextDueDate);
                      return (
                        <tr key={sched.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-3 font-semibold text-[#1e293b] whitespace-nowrap">{sched.machineName}</td>
                          <td className="px-4 py-3 text-slate-700 max-w-[220px] truncate">{sched.taskName}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border ${typeBadge(sched.scheduleType)}`}>
                              {sched.scheduleType === 'CUSTOM' && sched.frequencyDays ? `EVERY ${sched.frequencyDays} DAYS` : sched.scheduleType}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{sched.estimatedDurationMinutes} min</td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{sched.lastDoneDate || '—'}</td>
                          <td className={`px-4 py-3 whitespace-nowrap ${due.cls}`}>{due.text}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${sched.isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                              {sched.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => handleToggleActive(sched)}
                                className={`px-2.5 py-1 rounded-lg font-medium border text-xs inline-flex items-center gap-1 cursor-pointer transition-colors ${sched.isActive
                                    ? 'bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200'
                                    : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200'
                                  }`}
                                title={sched.isActive ? 'Deactivate' : 'Activate'}
                              >
                                <i className={sched.isActive ? 'ri-lock-line text-xs' : 'ri-lock-unlock-line text-xs'} />
                                {sched.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ CALENDAR TAB ═══ */}
          {activeTab === 'calendar' && (
            <div>
              {/* Top Filter Button Cards (Real Data & Interactive Filter) */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                <button
                  type="button"
                  onClick={() => setCalFilter('all')}
                  className={`p-4 rounded-xl border text-left cursor-pointer transition-all ${
                    calFilter === 'all'
                      ? 'bg-blue-50/90 border-blue-500 ring-2 ring-blue-200 shadow-sm'
                      : 'bg-white border-slate-200 hover:border-slate-300 shadow-xs'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">All Scheduled</p>
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  </div>
                  <p className="text-2xl font-bold text-[#1e293b] mt-1">{calStats.scheduled}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Click to view all tasks</p>
                </button>

                <button
                  type="button"
                  onClick={() => setCalFilter('pending')}
                  className={`p-4 rounded-xl border text-left cursor-pointer transition-all ${
                    calFilter === 'pending'
                      ? 'bg-amber-50/90 border-amber-500 ring-2 ring-amber-200 shadow-sm'
                      : 'bg-white border-slate-200 hover:border-slate-300 shadow-xs'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Pending / Due</p>
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  </div>
                  <p className="text-2xl font-bold text-amber-700 mt-1">{calStats.pending}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Active upcoming tasks</p>
                </button>

                <button
                  type="button"
                  onClick={() => setCalFilter('completed')}
                  className={`p-4 rounded-xl border text-left cursor-pointer transition-all ${
                    calFilter === 'completed'
                      ? 'bg-emerald-50/90 border-emerald-500 ring-2 ring-emerald-200 shadow-sm'
                      : 'bg-white border-slate-200 hover:border-slate-300 shadow-xs'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Completed</p>
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  </div>
                  <p className="text-2xl font-bold text-emerald-700 mt-1">{calStats.completed}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Finished work orders</p>
                </button>

                <button
                  type="button"
                  onClick={() => setCalFilter('overdue')}
                  className={`p-4 rounded-xl border text-left cursor-pointer transition-all ${
                    calFilter === 'overdue'
                      ? 'bg-rose-50/90 border-rose-500 ring-2 ring-rose-200 shadow-sm'
                      : 'bg-white border-slate-200 hover:border-slate-300 shadow-xs'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Overdue</p>
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                  </div>
                  <p className="text-2xl font-bold text-rose-700 mt-1">{calStats.overdue}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Passed deadline</p>
                </button>
              </div>

              {/* Calendar Controls */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <button onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(calMonth - 1); }} className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 cursor-pointer shadow-sm"><i className="ri-arrow-left-s-line" /></button>
                  <h3 className="text-lg font-bold text-[#1e293b] min-w-[180px] text-center">{MONTH_NAMES[calMonth]} {calYear}</h3>
                  <button onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(calMonth + 1); }} className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 cursor-pointer shadow-sm"><i className="ri-arrow-right-s-line" /></button>
                  <button
                    onClick={() => {
                      const nowObj = new Date();
                      setCalMonth(nowObj.getMonth());
                      setCalYear(nowObj.getFullYear());
                    }}
                    className="px-3 py-1 rounded-lg text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 cursor-pointer whitespace-nowrap"
                  >
                    Today
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
                    <button onClick={() => setCalView('month')} className={`px-3 py-1.5 rounded-md text-xs font-semibold cursor-pointer ${calView === 'month' ? 'bg-[#2563eb] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>Month</button>
                    <button onClick={() => setCalView('week')} className={`px-3 py-1.5 rounded-md text-xs font-semibold cursor-pointer ${calView === 'week' ? 'bg-[#2563eb] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>Week</button>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 mb-4 text-xs font-medium text-slate-600">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-rose-500" /> Overdue</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500" /> Due Today</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-500" /> Upcoming</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500" /> Completed</span>
              </div>

              {/* Month View */}
              {calView === 'month' && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="grid grid-cols-7 border-b border-slate-200">
                    {WEEK_DAYS.map((d) => (
                      <div key={d} className="text-center text-[11px] font-bold text-slate-500 uppercase py-3 bg-slate-50">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7">
                    {calDays.map((day, i) => {
                      if (day === null) return <div key={`empty-${i}`} className="h-24 border-b border-r border-slate-100 bg-slate-50/30 p-1" />;
                      const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const mwos = calMwoMap[dateStr] || [];
                      const realNow = new Date();
                      const isToday = day === realNow.getDate() && calYear === realNow.getFullYear() && calMonth === realNow.getMonth();
                      const isSel = selectedDay === day;
                      return (
                        <div key={day}
                          onClick={() => setSelectedDay(isSel ? null : day)}
                          className={`h-24 border-b border-r border-slate-100 p-1.5 overflow-hidden cursor-pointer transition-colors ${isToday ? 'bg-blue-50/60' : isSel ? 'bg-indigo-50/50' : 'hover:bg-slate-50'}`}
                        >
                          <p className={`text-xs font-bold mb-1 ${isToday ? 'text-blue-700 bg-blue-100 w-6 h-6 flex items-center justify-center rounded-full' : 'text-slate-600'}`}>{day}</p>
                          {mwos.slice(0, 2).map((mwo) => (
                            <div key={mwo.id}
                              onClick={(e) => { e.stopPropagation(); setViewMwo(mwo); }}
                              className={`text-[10px] font-medium px-1.5 py-0.5 rounded truncate mb-0.5 cursor-pointer ${getMwoPillColor(mwo)}`}
                            >
                              [{mwo.machineName ? mwo.machineName.split(' ').pop() : ''}] {mwo.taskName.slice(0, 14)}
                            </div>
                          ))}
                          {mwos.length > 2 && <p className="text-[10px] text-slate-400 pl-1 font-medium">+{mwos.length - 2} more</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Week View */}
              {calView === 'week' && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                    {calWeekDays.map((wd) => (
                      <div key={wd.dateStr} className={`text-center py-3 border-r border-slate-100 last:border-r-0 ${wd.dateStr === today ? 'bg-blue-50/70' : ''}`}>
                        <p className="text-[11px] text-slate-400 font-semibold">{wd.date.toLocaleDateString('en', { weekday: 'short' })}</p>
                        <p className={`text-sm font-bold mt-0.5 ${wd.dateStr === today ? 'text-blue-700' : 'text-slate-800'}`}>{wd.date.getDate()}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7">
                    {calWeekDays.map((wd) => {
                      const mwos = calMwoMap[wd.dateStr] || [];
                      return (
                        <div key={wd.dateStr} className="min-h-[200px] border-r border-slate-100 last:border-r-0 p-1.5">
                          {mwos.slice(0, 4).map((mwo) => (
                            <div key={mwo.id} onClick={() => setViewMwo(mwo)} className={`text-[10px] font-medium px-1.5 py-0.5 rounded mb-1 cursor-pointer truncate ${getMwoPillColor(mwo)}`}>
                              {mwo.taskName.slice(0, 18)}
                            </div>
                          ))}
                          {mwos.length > 4 && <p className="text-[10px] text-slate-400 font-medium">+{mwos.length - 4} more</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══ AMC CONTRACTS TAB ═══ */}
          {activeTab === 'amc' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-[#1e293b]">Annual Maintenance Contracts</h3>
                  <p className="text-xs text-slate-500">Manage vendor servicing agreements & equipment warranties</p>
                </div>
                <button
                  onClick={() => navigate('/manufacturing/maintenance/amc/new')}
                  className="px-3.5 py-1.5 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-xs font-semibold cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 shadow-sm"
                >
                  <i className="ri-add-line text-sm" /> Add AMC Contract
                </button>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/70">
                        {['Contract No', 'Vendor', 'Machines', 'Type', 'Start', 'End', 'Annual Cost', 'Next Visit', 'Status', 'Actions'].map((h) => (
                          <th key={h} className={`text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${h === 'Actions' ? 'text-right' : ''}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {amcList.map((amc) => (
                        <tr key={amc.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors">
                          <td className="px-4 py-3 font-semibold text-[#1e293b] whitespace-nowrap">{amc.contractNumber}</td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap font-medium">{amc.vendorName}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{amc.machineNames.join(', ')}</td>
                          <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border ${amcTypeBadge(amc.contractType)}`}>{amc.contractType.replace('_', ' ')}</span></td>
                          <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{amc.startDate}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{amc.endDate}</td>
                          <td className="px-4 py-3 text-slate-800 font-bold whitespace-nowrap">₹{amc.annualCost.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{amc.nextVisitDate || '—'}</td>
                          <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border ${amcStatusBadge(amc.status)}`}>{amc.status.replace('_', ' ')}</span></td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button onClick={() => setViewAmc(amc)} className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium border border-slate-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors" title="View"><i className="ri-eye-line text-xs" /> View</button>
                              <button onClick={() => navigate('/manufacturing/maintenance/amc/new', { state: { renewContract: amc } })} className="px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold border border-emerald-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors" title="Renew"><i className="ri-refresh-line text-xs" /> Renew</button>
                              {amc.nextVisitDate && (
                                <button onClick={() => handleScheduleVisit(amc)} className="px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold border border-blue-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors" title="Schedule Visit"><i className="ri-calendar-event-line text-xs" /> Schedule Visit</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ═══ SPARE PARTS TAB ═══ */}
          {activeTab === 'spares' && (
            <div className="space-y-6">
              {/* Filter & Add Button Bar */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 w-full md:w-auto flex-1">
                  <div className="relative flex-1 max-w-md">
                    <i className="ri-search-line absolute left-3.5 top-2.5 text-slate-400 text-sm" />
                    <input
                      type="text"
                      placeholder="Search by part code, part name, rack location..."
                      value={sparesSearch}
                      onChange={(e) => setSparesSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                    />
                  </div>
                  <div className="w-52">
                    <SearchableSelect
                      value={sparesCategoryFilter}
                      onChange={(val) => setSparesCategoryFilter(val)}
                      options={sparesCategoryFilterOptions}
                      placeholder="All Categories"
                    />
                  </div>
                </div>

                <button
                  onClick={handleOpenAddSpare}
                  className="px-4 py-2 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-semibold cursor-pointer shadow-sm flex items-center gap-1.5 whitespace-nowrap"
                >
                  <i className="ri-add-line text-base" /> Add Spare Part
                </button>
              </div>

              {/* Spare Parts Table */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/70 text-slate-500 uppercase tracking-wider text-xs font-semibold">
                        <th className="text-left px-4 py-3">Part Code</th>
                        <th className="text-left px-4 py-3">Part Name</th>
                        <th className="text-left px-4 py-3">Category</th>
                        <th className="text-center px-4 py-3">Stock Qty</th>
                        <th className="text-right px-4 py-3">Unit Cost</th>
                        <th className="text-left px-4 py-3">Rack Location</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-right px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredSpareParts.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-10 text-slate-400">
                            <i className="ri-inbox-archive-line text-3xl block mb-2" />
                            No spare parts found. Click "Add Spare Part" to create one.
                          </td>
                        </tr>
                      ) : (
                        filteredSpareParts.map((sp) => {
                          const isLowStock = sp.stockQty <= sp.minStockLevel;
                          return (
                            <tr key={sp.id} className={`hover:bg-slate-50/80 transition-colors ${isLowStock ? 'bg-rose-50/30' : ''}`}>
                              <td className="px-4 py-3 font-bold text-slate-800 whitespace-nowrap">{sp.partCode}</td>
                              <td className="px-4 py-3 font-semibold text-slate-900">
                                <div>{sp.partName}</div>
                                {/* {sp.vendorName && (
                                  <div className="text-[11px] text-slate-400 font-normal">Vendor: {sp.vendorName}</div>
                                )} */}
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                  {sp.category}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center whitespace-nowrap">
                                <span className={`font-bold text-sm ${isLowStock ? 'text-rose-600' : 'text-slate-800'}`}>
                                  {sp.stockQty} {sp.unit}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-slate-800 whitespace-nowrap">
                                ₹{sp.unitCost.toLocaleString('en-IN')}
                              </td>
                              <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                                {sp.rackLocation || '—'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {isLowStock ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-50 text-rose-700 text-[11px] font-bold border border-rose-200">
                                    <i className="ri-error-warning-line text-xs" /> Low Stock (Min: {sp.minStockLevel})
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-semibold border border-emerald-200">
                                    <i className="ri-checkbox-circle-line text-xs" /> In Stock
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1.5">
                                  <button
                                    onClick={() => { setRestockSpare(sp); setRestockQty(10); }}
                                    className="px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold border border-emerald-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                                    title="Restock"
                                  >
                                    <i className="ri-add-circle-line text-xs" /> Restock
                                  </button>
                                  <button
                                    onClick={() => handleOpenEditSpare(sp)}
                                    className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium border border-slate-200 text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                                    title="Edit"
                                  >
                                    <i className="ri-edit-line text-xs" /> Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSparePart(sp)}
                                    className="p-1 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors cursor-pointer"
                                    title="Delete"
                                  >
                                    <i className="ri-delete-bin-line text-sm" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* ═══ COMPLETE MWO MODAL ═══ */}
        {completeMwo && (
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
            <div className="absolute inset-0 bg-black/40" onClick={() => setCompleteMwo(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 bg-slate-50">
                <h2 className="text-lg font-bold text-[#1e293b]">Complete {completeMwo.mwoNumber} — {completeMwo.taskName}</h2>
                <p className="text-sm text-slate-500 mt-0.5">Machine: {completeMwo.machineName}</p>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <h4 className="text-sm font-bold text-[#1e293b] mb-3">Task Checklist</h4>
                  <div className="space-y-2">
                    {getScheduleForMwo(completeMwo)?.tasks.map((task) => (
                      <label key={task.id} className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                        <input type="checkbox" checked={checkedTasks.includes(task.id)} onChange={(e) => { if (e.target.checked) setCheckedTasks((p) => [...p, task.id]); else setCheckedTasks((p) => p.filter((id) => id !== task.id)); }} className="mt-0.5 w-4 h-4 rounded text-[#2563eb] cursor-pointer" />
                        <div><p className="text-sm font-semibold text-[#1e293b]">{task.taskName}</p><p className="text-xs text-slate-500">{task.description}</p></div>
                      </label>
                    ))}
                  </div>
                </div>
                {sparesUsed.length > 0 && (
                  <div>
                    <h4 className="text-sm font-bold text-[#1e293b] mb-3">Spares Used</h4>
                    <div className="space-y-2">
                      {sparesUsed.map((sp, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
                          <span className="text-sm font-medium text-slate-700 flex-1">{sp.itemName}</span>
                          <input type="number" value={sp.qty} min="0" step="0.01" onChange={(e) => { const u = [...sparesUsed]; u[i] = { ...u[i], qty: parseFloat(e.target.value) || 0 }; setSparesUsed(u); }} className="w-20 px-2 py-1.5 rounded-lg border border-slate-300 text-sm text-center text-[#1e293b] font-semibold bg-white" />
                          <span className="text-xs text-slate-500 font-medium w-10">{sp.unit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div><h4 className="text-sm font-bold text-[#1e293b] mb-2">Findings</h4><textarea value={findings} onChange={(e) => setFindings(e.target.value)} placeholder="What did you find?" maxLength={500} rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" /></div>
                <div><h4 className="text-sm font-bold text-[#1e293b] mb-2">Next Action</h4><textarea value={nextAction} onChange={(e) => setNextAction(e.target.value)} placeholder="Any action before next service?" maxLength={500} rows={2} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" /></div>
                <div>
                  <h4 className="text-sm font-bold text-[#1e293b] mb-2">Time Tracking</h4>
                  <div className="flex items-center gap-4">
                    <div><p className="text-[11px] font-semibold text-slate-500 mb-1">End Time</p><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" /></div>
                    <div className="pt-5"><span className="text-sm font-semibold text-slate-600">Duration: ~{(() => { const s = completeMwo.actualStartTime ? new Date(completeMwo.actualStartTime) : new Date(); const e = new Date(); e.setHours(parseInt(endTime.split(':')[0]), parseInt(endTime.split(':')[1])); return Math.round((e.getTime() - s.getTime()) / 60000); })()} min</span></div>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3 bg-slate-50">
                <button onClick={() => setCompleteMwo(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-100 cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={handleCompleteMwo} className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold cursor-pointer whitespace-nowrap shadow-sm">Mark as Completed</button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ SKIP MWO MODAL ═══ */}
        {skipMwo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setSkipMwo(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
              <h2 className="text-lg font-bold text-[#1e293b] mb-1">Skip {skipMwo.mwoNumber}</h2>
              <p className="text-sm text-slate-500 mb-4">{skipMwo.taskName} — {skipMwo.machineName}</p>
              <textarea value={skipReason} onChange={(e) => setSkipReason(e.target.value)} placeholder="Reason for skipping..." maxLength={500} rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" />
              <div className="flex items-center justify-end gap-3 mt-5">
                <button onClick={() => setSkipMwo(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={handleSkipMwo} className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold cursor-pointer whitespace-nowrap shadow-sm">Skip Work Order</button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ CREATE NEW SCHEDULE MODAL ═══ */}
        {showNewSchedule && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowNewSchedule(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh]">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <div>
                  <h2 className="text-lg font-bold text-[#1e293b]">Create Maintenance Schedule</h2>
                  <p className="text-xs text-slate-500">Define recurring servicing rules & auto-generate initial MWO</p>
                </div>
                <button onClick={() => setShowNewSchedule(false)} className="w-8 h-8 rounded-lg hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto">
                {/* Machine & Schedule Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Select Machine *</label>
                    <SearchableSelect
                      value={newMachineId}
                      onChange={(selId) => {
                        setNewMachineId(selId);
                        const found = liveMachines.find((m) => m.id === selId);
                        setNewMachineName(found ? found.name : selId);
                      }}
                      options={machineSelectOptions}
                      placeholder="-- Select Target Machine --"
                      searchPlaceholder="Search machine..."
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Servicing Frequency *</label>
                    <SearchableSelect
                      value={newScheduleType}
                      onChange={(val) => setNewScheduleType(val as any)}
                      options={frequencyOptions}
                      placeholder="Select Frequency"
                    />
                  </div>
                </div>

                {/* Task Name & Duration */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Servicing Task Title *</label>
                    <input
                      type="text"
                      placeholder="e.g. Daily Pre-shift Safety & Lubrication Inspection"
                      value={newTaskName}
                      onChange={(e) => setNewTaskName(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Est. Duration (Mins)</label>
                    <input
                      type="number"
                      value={newDuration}
                      onChange={(e) => setNewDuration(parseInt(e.target.value) || 30)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                    />
                  </div>
                </div>

                {/* Technician Assignment, First Due Date, & Advance Alert Days */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Assign Technician / Operator</label>
                    <SearchableSelect
                      value={newAssignedToId}
                      onChange={(selId) => {
                        setNewAssignedToId(selId);
                        const found = liveOperators.find((o) => o.id === selId);
                        setNewAssignedToName(found ? found.name : selId);
                      }}
                      options={operatorSelectOptions}
                      placeholder="-- Select Assignee --"
                      searchPlaceholder="Search technician/operator..."
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">First Servicing Due Date *</label>
                    <input
                      type="date"
                      value={newNextDueDate}
                      onChange={(e) => setNewNextDueDate(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Advance Alert (Days)</label>
                    <input
                      type="number"
                      value={newAdvanceAlertDays}
                      onChange={(e) => setNewAdvanceAlertDays(parseInt(e.target.value) || 0)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                      placeholder="e.g. 7 days before"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Detailed Servicing Instructions</label>
                  <textarea
                    rows={2}
                    placeholder="Check oil sight glass, inspect safety guards, apply grease to guide pillars..."
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                  />
                </div>

                {/* Inspection Checklist Builder */}
                <div className="pt-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Inspection Checklist & Spares Requirements</label>
                    <button
                      type="button"
                      onClick={() => setNewChecklist([
                        ...newChecklist,
                        { id: `t_${Date.now()}`, taskName: '', description: '', requiresSpares: false, spareParts: [] }
                      ])}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-700 cursor-pointer inline-flex items-center gap-1"
                    >
                      <i className="ri-add-line" /> Add Task Point
                    </button>
                  </div>

                  <div className="space-y-3 max-h-[220px] overflow-y-auto pr-1">
                    {newChecklist.map((item, idx) => (
                      <div key={item.id} className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-400 w-5">{idx + 1}.</span>
                          <input
                            type="text"
                            placeholder="Check point title (e.g. Check hydraulic oil level)..."
                            value={item.taskName}
                            onChange={(e) => {
                              const updated = [...newChecklist];
                              updated[idx].taskName = e.target.value;
                              setNewChecklist(updated);
                            }}
                            className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (newChecklist.length === 1) return;
                              setNewChecklist(newChecklist.filter((_, i) => i !== idx));
                            }}
                            className="w-7 h-7 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>

                        {/* Requires Spares Toggle */}
                        <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-200/60">
                          <label className="flex items-center gap-2 text-slate-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={item.requiresSpares}
                              onChange={(e) => {
                                const updated = [...newChecklist];
                                updated[idx].requiresSpares = e.target.checked;
                                if (e.target.checked && updated[idx].spareParts.length === 0) {
                                  updated[idx].spareParts = [{ itemName: 'Machine Lubricant / Oil', qty: 1, unit: 'Ltr' }];
                                }
                                setNewChecklist(updated);
                              }}
                              className="rounded text-blue-600 focus:ring-blue-500"
                            />
                            <span className="font-semibold text-slate-700">Requires Spare Parts for this task</span>
                          </label>

                          {item.requiresSpares && (
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...newChecklist];
                                updated[idx].spareParts.push({ itemName: '', qty: 1, unit: 'Pcs' });
                                setNewChecklist(updated);
                              }}
                              className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
                            >
                              + Add Spare Item
                            </button>
                          )}
                        </div>

                        {/* Spare Parts Rows */}
                        {item.requiresSpares && item.spareParts.length > 0 && (
                          <div className="space-y-1.5 pt-1">
                            {item.spareParts.map((sp, sIdx) => (
                              <div key={sIdx} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-200">
                                <input
                                  type="text"
                                  placeholder="Spare item name (e.g. Grease / Filter)..."
                                  value={sp.itemName}
                                  onChange={(e) => {
                                    const updated = [...newChecklist];
                                    updated[idx].spareParts[sIdx].itemName = e.target.value;
                                    setNewChecklist(updated);
                                  }}
                                  className="flex-1 px-2.5 py-1 rounded border border-slate-200 text-xs focus:outline-none"
                                />
                                <input
                                  type="number"
                                  placeholder="Qty"
                                  value={sp.qty}
                                  onChange={(e) => {
                                    const updated = [...newChecklist];
                                    updated[idx].spareParts[sIdx].qty = parseFloat(e.target.value) || 1;
                                    setNewChecklist(updated);
                                  }}
                                  className="w-16 px-2 py-1 rounded border border-slate-200 text-xs focus:outline-none text-center font-medium"
                                />
                                <input
                                  type="text"
                                  placeholder="Unit"
                                  value={sp.unit}
                                  onChange={(e) => {
                                    const updated = [...newChecklist];
                                    updated[idx].spareParts[sIdx].unit = e.target.value;
                                    setNewChecklist(updated);
                                  }}
                                  className="w-16 px-2 py-1 rounded border border-slate-200 text-xs focus:outline-none text-center font-medium"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...newChecklist];
                                    updated[idx].spareParts.splice(sIdx, 1);
                                    if (updated[idx].spareParts.length === 0) updated[idx].requiresSpares = false;
                                    setNewChecklist(updated);
                                  }}
                                  className="text-slate-400 hover:text-rose-600 p-1"
                                >
                                  <i className="ri-close-line text-sm" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowNewSchedule(false)}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-100 cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSchedule}
                  className="px-5 py-2 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-semibold cursor-pointer whitespace-nowrap shadow-sm inline-flex items-center gap-1.5"
                >
                  <i className="ri-save-line text-sm" /> Save & Auto-Generate MWO
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ MWO DETAIL SLIDE-OVER ═══ */}
        {viewMwo && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={() => setViewMwo(null)} />
            <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col overflow-y-auto">
              <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
                <h2 className="text-lg font-bold text-[#1e293b]">{viewMwo.mwoNumber}</h2>
                <button onClick={() => setViewMwo(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500 cursor-pointer"><i className="ri-close-line text-lg" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Task</p><p className="text-sm font-bold text-[#1e293b] mt-0.5">{viewMwo.taskName}</p></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Machine</p><p className="text-sm font-semibold text-[#1e293b] mt-0.5">{viewMwo.machineName}</p></div>
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Type</p><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border mt-0.5 ${typeBadge(viewMwo.scheduleType)}`}>{viewMwo.scheduleType}</span></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Scheduled</p><p className="text-sm font-semibold text-[#1e293b] mt-0.5">{viewMwo.scheduledDate}</p></div>
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Status</p><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border mt-0.5 ${statusBadge(viewMwo.status)}`}>{viewMwo.status.replace('_', ' ')}</span></div>
                </div>
                <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Assigned To</p><p className="text-sm font-semibold text-[#1e293b] mt-0.5">{viewMwo.assignedTo}</p></div>
                {viewMwo.durationMinutes && <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Duration</p><p className="text-sm font-semibold text-[#1e293b] mt-0.5">{viewMwo.durationMinutes} min</p></div>}
                {viewMwo.findings && <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Findings</p><p className="text-sm text-slate-700 mt-0.5">{viewMwo.findings}</p></div>}
                {viewMwo.nextActionRequired && <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Next Action</p><p className="text-sm text-slate-700 mt-0.5">{viewMwo.nextActionRequired}</p></div>}
                {viewMwo.sparesUsed.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Spares Used</p>
                    <div className="space-y-1 bg-slate-50 p-3 rounded-lg border border-slate-200">
                      {viewMwo.sparesUsed.map((sp, i) => (
                        <div key={i} className="flex justify-between text-sm text-slate-700 font-medium"><span>{sp.itemName}</span><span className="font-bold">{sp.qty} {sp.unit}</span></div>
                      ))}
                    </div>
                  </div>
                )}
                {viewMwo.skipReason && <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Skip Reason</p><p className="text-sm text-slate-600 italic mt-0.5">{viewMwo.skipReason}</p></div>}
              </div>
            </div>
          </div>
        )}

        {/* ═══ AMC VIEW SLIDE-OVER ═══ */}
        {viewAmc && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={() => setViewAmc(null)} />
            <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col overflow-y-auto">
              <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
                <h2 className="text-lg font-bold text-[#1e293b]">{viewAmc.contractNumber}</h2>
                <button onClick={() => setViewAmc(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500 cursor-pointer"><i className="ri-close-line text-lg" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-2"><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border ${amcStatusBadge(viewAmc.status)}`}>{viewAmc.status.replace('_', ' ')}</span><span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border ${amcTypeBadge(viewAmc.contractType)}`}>{viewAmc.contractType.replace('_', ' ')}</span></div>
                <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Vendor</p><p className="text-sm font-bold text-[#1e293b] mt-0.5">{viewAmc.vendorName}</p></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Phone</p><p className="text-sm text-slate-700 mt-0.5">{viewAmc.vendorPhone}</p></div>
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Email</p><p className="text-sm text-slate-700 mt-0.5">{viewAmc.vendorEmail}</p></div>
                </div>
                <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Machines Covered</p><p className="text-sm font-semibold text-[#1e293b] mt-0.5">{viewAmc.machineNames.join(', ')}</p></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Start Date</p><p className="text-sm font-semibold text-[#1e293b] mt-0.5">{viewAmc.startDate}</p></div>
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">End Date</p><p className="text-sm font-semibold text-[#1e293b] mt-0.5">{viewAmc.endDate}</p></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Annual Cost</p><p className="text-sm font-bold text-[#1e293b] mt-0.5">₹{viewAmc.annualCost.toLocaleString('en-IN')}</p></div>
                  <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Visit Frequency</p><p className="text-sm text-slate-700 mt-0.5">{viewAmc.visitFrequency}</p></div>
                </div>
                <div><p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Next Visit</p><p className="text-sm font-semibold text-[#1e293b] mt-0.5">{viewAmc.nextVisitDate || 'Not scheduled'}</p></div>
              </div>
            </div>
          </div>
        )}

        {/* ═══ RENEW AMC MODAL ═══ */}
        {renewAmc && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setRenewAmc(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
              <h2 className="text-lg font-bold text-[#1e293b] mb-1">Renew {renewAmc.contractNumber}</h2>
              <p className="text-sm text-slate-500 mb-4">{renewAmc.vendorName} — {renewAmc.machineNames.join(', ')}</p>
              <div className="space-y-4">
                <div><label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">New Start Date</label><input type="date" value={renewStartDate} onChange={(e) => setRenewStartDate(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" /></div>
                <div><label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">New End Date</label><input type="date" value={renewEndDate} onChange={(e) => setRenewEndDate(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" /></div>
              </div>
              <div className="flex items-center justify-end gap-3 mt-6">
                <button onClick={() => setRenewAmc(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={handleRenewAmc} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold cursor-pointer whitespace-nowrap shadow-sm">Renew Contract</button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ REPORTS SLIDE-OVER ═══ */}
        {showReports && (
          <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={() => setShowReports(false)} />
            <div className="relative w-full max-w-2xl bg-white h-full shadow-2xl flex flex-col overflow-y-auto">
              <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
                <h2 className="text-lg font-bold text-[#1e293b]">Maintenance Reports</h2>
                <button onClick={() => setShowReports(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500 cursor-pointer"><i className="ri-close-line text-lg" /></button>
              </div>
              <div className="p-5 space-y-5">
                {/* Report Type Selector */}
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Report Type</label>
                  <div className="flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-lg p-1 w-fit">
                    {(['completion', 'spares', 'cost'] as const).map((t) => (
                      <button key={t} onClick={() => setReportType(t)}
                        className={`px-4 py-2 rounded-md text-xs font-semibold cursor-pointer whitespace-nowrap ${reportType === t ? 'bg-[#2563eb] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                      >
                        {t === 'completion' ? 'Completion Rate' : t === 'spares' ? 'Spares Consumption' : 'Maintenance Cost'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Period */}
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Period</label>
                  <input type="month" value={reportPeriod} onChange={(e) => setReportPeriod(e.target.value)} className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300" />
                </div>

                {/* Report 1: Completion Rate */}
                {reportType === 'completion' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold text-[#1e293b]">Completion Rate Report</h3>
                      <div className={`text-2xl font-bold ${reportData.overallRate > 90 ? 'text-emerald-600' : reportData.overallRate > 70 ? 'text-amber-600' : 'text-rose-600'}`}>
                        {reportData.overallRate}%
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            {['Machine', 'Scheduled', 'Completed', 'Overdue', 'Rate'].map((h) => (
                              <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.completionRows.map((r) => (
                            <tr key={r.machine} className="border-b border-slate-100">
                              <td className="px-4 py-2.5 text-sm font-semibold text-[#1e293b]">{r.machine}</td>
                              <td className="px-4 py-2.5 text-sm text-slate-700">{r.scheduled}</td>
                              <td className="px-4 py-2.5 text-sm text-emerald-700 font-bold">{r.completed}</td>
                              <td className="px-4 py-2.5 text-sm text-rose-600 font-bold">{r.overdue}</td>
                              <td className="px-4 py-2.5"><span className={`text-sm font-bold ${r.rate > 90 ? 'text-emerald-600' : r.rate > 70 ? 'text-amber-600' : 'text-rose-600'}`}>{r.rate}%</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button onClick={() => exportCsv(['Machine', 'Scheduled', 'Completed', 'Overdue', 'Rate%'], reportData.completionRows.map((r) => [r.machine, String(r.scheduled), String(r.completed), String(r.overdue), String(r.rate)]), `Maintenance_Completion_${reportPeriod}.csv`)} className="mt-3 px-4 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-sm font-semibold text-slate-700 cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5">
                      <i className="ri-download-line text-sm" /> Export CSV
                    </button>
                  </div>
                )}

                {/* Report 2: Spares Consumption */}
                {reportType === 'spares' && (
                  <div>
                    <h3 className="text-sm font-bold text-[#1e293b] mb-4">Spares Consumption Report</h3>
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            {['Spare Item', 'Total Qty Used', 'Est. Cost', 'Used In'].map((h) => (
                              <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.sparesRows.map((r) => (
                            <tr key={r.item} className="border-b border-slate-100">
                              <td className="px-4 py-2.5 text-sm font-semibold text-[#1e293b]">{r.item}</td>
                              <td className="px-4 py-2.5 text-sm text-slate-700 font-medium">{r.qty} {r.unit}</td>
                              <td className="px-4 py-2.5 text-sm text-slate-800 font-bold">₹{r.cost.toLocaleString('en-IN')}</td>
                              <td className="px-4 py-2.5 text-xs text-slate-500">{r.usedIn}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button onClick={() => exportCsv(['Spare Item', 'Qty Used', 'Est. Cost', 'Used In'], reportData.sparesRows.map((r) => [r.item, `${r.qty} ${r.unit}`, `₹${r.cost}`, r.usedIn]), `Spares_Consumption_${reportPeriod}.csv`)} className="mt-3 px-4 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-sm font-semibold text-slate-700 cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5">
                      <i className="ri-download-line text-sm" /> Export CSV
                    </button>
                  </div>
                )}

                {/* Report 3: Machine Cost */}
                {reportType === 'cost' && (
                  <div>
                    <h3 className="text-sm font-bold text-[#1e293b] mb-4">Machine Maintenance Cost Report</h3>
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            {['Machine', 'AMC Cost', 'Spares Cost', 'Labour', 'Total'].map((h) => (
                              <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {activeMachines.map((m) => {
                            const amc = amcList.find((a) => a.machineIds.includes(m.id));
                            const mwos = mwoList.filter((mw) => (mw.machineId === m.id || mw.machineName === m.name) && mw.status === 'COMPLETED');
                            const sparesCost = mwos.reduce((s, mw) => s + mw.sparesUsed.reduce((ss, sp) => ss + sp.qty * 500, 0), 0);
                            const labourCost = mwos.reduce((s, mw) => s + (mw.durationMinutes || 0) * 8, 0);
                            const amcCost = amc ? amc.annualCost / 12 : 0;
                            return (
                              <tr key={m.id} className="border-b border-slate-100">
                                <td className="px-4 py-2.5 text-sm font-semibold text-[#1e293b]">{m.name}</td>
                                <td className="px-4 py-2.5 text-sm text-slate-700 font-medium">₹{amcCost.toLocaleString('en-IN')}</td>
                                <td className="px-4 py-2.5 text-sm text-slate-700 font-medium">₹{sparesCost.toLocaleString('en-IN')}</td>
                                <td className="px-4 py-2.5 text-sm text-slate-700 font-medium">₹{labourCost.toLocaleString('en-IN')}</td>
                                <td className="px-4 py-2.5 text-sm font-bold text-[#1e293b]">₹{(amcCost + sparesCost + labourCost).toLocaleString('en-IN')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <button onClick={() => exportCsv(['Machine', 'AMC Cost', 'Spares Cost', 'Labour', 'Total'], activeMachines.map((m) => {
                      const amc = amcList.find((a) => a.machineIds.includes(m.id));
                      const mwos = mwoList.filter((mw) => (mw.machineId === m.id || mw.machineName === m.name) && mw.status === 'COMPLETED');
                      const sc = mwos.reduce((s, mw) => s + mw.sparesUsed.reduce((ss, sp) => ss + sp.qty * 500, 0), 0);
                      const lc = mwos.reduce((s, mw) => s + (mw.durationMinutes || 0) * 8, 0);
                      const ac = amc ? amc.annualCost / 12 : 0;
                      return [m.name, `₹${ac}`, `₹${sc}`, `₹${lc}`, `₹${ac + sc + lc}`];
                    }), `Machine_Maintenance_Cost_${reportPeriod}.csv`)} className="mt-3 px-4 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-sm font-semibold text-slate-700 cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5">
                      <i className="ri-download-line text-sm" /> Export CSV
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═══ CREATE / EDIT SPARE PART MODAL ═══ */}
        {showSpareModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowSpareModal(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden flex flex-col max-h-[92vh]">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <div>
                  <h2 className="text-lg font-bold text-[#1e293b]">
                    {editingSpare ? `Edit Spare Part: ${editingSpare.partCode}` : 'Add New Spare Part'}
                  </h2>
                  <p className="text-xs text-slate-500">Register stock items, unit costs, and storage rack locations</p>
                </div>
                <button onClick={() => setShowSpareModal(false)} className="w-8 h-8 rounded-lg hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide">Part Code *</label>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Auto Generated</span>
                    </div>
                    <input
                      type="text"
                      value={spCode}
                      readOnly
                      disabled
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-700 text-sm font-bold cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Category *</label>
                    <SearchableSelect
                      value={spCategory}
                      onChange={(val) => setSpCategory(val)}
                      options={spareCategoryFormOptions}
                      placeholder="Select Category"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Part Name *</label>
                  <input
                    type="text"
                    placeholder="e.g. 6204-ZZ Deep Groove Ball Bearing"
                    value={spName}
                    onChange={(e) => setSpName(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Initial Stock Qty</label>
                    <input
                      type="number"
                      min={0}
                      value={spStockQty}
                      onChange={(e) => setSpStockQty(parseInt(e.target.value) || 0)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Unit *</label>
                    <SearchableSelect
                      value={spUnit}
                      onChange={(val) => setSpUnit(val)}
                      options={spareUnitOptions}
                      placeholder="Select Unit"
                      searchPlaceholder="Search unit..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Unit Cost (₹)</label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={spUnitCost}
                      onChange={(e) => setSpUnitCost(parseFloat(e.target.value) || 0)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Min Stock Alert Level</label>
                    <input
                      type="number"
                      min={1}
                      value={spMinStock}
                      onChange={(e) => setSpMinStock(parseInt(e.target.value) || 5)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Rack Location</label>
                    <input
                      type="text"
                      placeholder="e.g. Rack B2, Bin 4"
                      value={spRackLoc}
                      onChange={(e) => setSpRackLoc(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                    />
                  </div>
                </div>

                {/* Vendor Field Disabled / Commented out in Frontend */}
                {/* <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Supplier / Vendor Name</label>
                  <input
                    type="text"
                    placeholder="e.g. National Bearings Corporation"
                    value={spVendor}
                    onChange={(e) => setSpVendor(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                  />
                </div> */}

                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Description / Notes</label>
                  <textarea
                    rows={2}
                    placeholder="Additional technical details, dimensions, or compatibility details..."
                    value={spDesc}
                    onChange={(e) => setSpDesc(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-3 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setShowSpareModal(false)}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-100 cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveSparePart}
                  className="px-5 py-2 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-semibold cursor-pointer whitespace-nowrap shadow-sm"
                >
                  {editingSpare ? 'Update Spare Part' : 'Save Spare Part'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ QUICK RESTOCK MODAL ═══ */}
        {restockSpare && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setRestockSpare(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-bold text-[#1e293b] mb-1">Restock Inventory</h2>
              <p className="text-sm text-slate-500 mb-4">
                {restockSpare.partCode} — {restockSpare.partName}
              </p>

              <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 mb-4 flex justify-between items-center text-sm">
                <span className="text-slate-600 font-medium">Current Stock:</span>
                <span className="font-bold text-slate-800">{restockSpare.stockQty} {restockSpare.unit}</span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase tracking-wide mb-1">Quantity to Add *</label>
                <input
                  type="number"
                  min={1}
                  value={restockQty}
                  onChange={(e) => setRestockQty(parseInt(e.target.value) || 1)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-bold"
                />
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setRestockSpare(null)}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-100 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRestockSpare}
                  className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold cursor-pointer shadow-sm inline-flex items-center gap-1.5"
                >
                  <i className="ri-add-circle-line text-base" /> Confirm Restock
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}